import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { fetchBillingAccounts } from '../../../common/fetch.js';
import { nextMsg, t } from '../../../common/i18n/index.js';
import { findPersonBillingAccount } from '../../../common/utils/stateUtils.js';
import { Loading } from '../../Loading/index.js';
import {
  PersonalBillingDetails,
  createPersonBillingDetailsData,
} from '../../PersonalBillingDetails/PersonalBillingDetails.js';
import { TermsAndConditions } from '../../TermsAndConditions/TermsAndConditions.js';
import type { BillingAccount } from '../../../generated/api/models.js';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { PersonalBillingDetailsData } from '../../PersonalBillingDetails/PersonalBillingDetails.js';

type PunchoutCheckoutBillingProps = {
  onClickNext: (values: PersonalBillingDetailsData) => void;
  onSetBillingTerms: (termsAccepted: boolean) => void;
  user: AuthenticatedUser;
  isBillingTermsAccepted?: boolean;
};

export const PunchoutCheckoutBilling = ({
  onClickNext,
  user,
  isBillingTermsAccepted,
  onSetBillingTerms,
}: PunchoutCheckoutBillingProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [billingAccounts, setBillingAccounts] = useState<BillingAccount[] | null>(null);
  const [fetchError, setFetchError] = useState(false);
  const personBillingAccount = billingAccounts ? findPersonBillingAccount(billingAccounts) : undefined;

  const personBillingDetailsData = createPersonBillingDetailsData(user, personBillingAccount);

  const methods = useForm<PersonalBillingDetailsData>({
    values: personBillingDetailsData,
  });

  useEffect(() => {
    fetchBillingAccounts({ useSearchService: true }, { isPunchout: true })
      .then(response => {
        setBillingAccounts(response.billingAccounts || []);
      })
      .catch(() => {
        setFetchError(true);
        setBillingAccounts([]);
      });
  }, []);

  const handleSubmit = (values: PersonalBillingDetailsData) => {
    setIsLoading(true);
    onClickNext(values);
  };
  if (!billingAccounts) {
    return <Loading />;
  }

  return (
    <div>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <PersonalBillingDetails editing={!personBillingAccount} data={personBillingDetailsData} enableEditing />
          <TermsAndConditions
            onAcceptance={() => onSetBillingTerms(!isBillingTermsAccepted)}
            isAccepted={isBillingTermsAccepted}
          />
          {fetchError && (
            <CL.Disclaimer
              className={`${dsClass.PADDING_6} ${dsClass.MARGIN_BOTTOM_3}`}
              disclaimerType="alert"
              icon={<CL.Icon icon="warning" size="l" aria-hidden="true" />}
              text={t.L7BV('We could not update your changes. Please try again later.')}
              visible
            />
          )}
          <CL.Button size="l" className={dsClass.MARGIN_TOP_2} loading={isLoading} type="submit">
            {t.F0MY(nextMsg)}
          </CL.Button>
        </form>
      </FormProvider>
    </div>
  );
};
